import React, { useId, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Card } from './Card';

export interface CollapsibleSectionProps {
  /** Section title. When passing ReactNode, caller must ensure content is safe
   *  — do not pass unsanitized user input. No interactive elements (buttons, links). */
  title: React.ReactNode;
  /** Optional count to display next to title */
  count?: number;
  /** Whether section is expanded by default */
  defaultExpanded?: boolean;
  /** Controlled expanded state (overrides internal state) */
  expanded?: boolean;
  /** Called when expanded state changes */
  onExpandedChange?: (expanded: boolean) => void;
  /** Content to render in the section */
  children: React.ReactNode;
  /** Optional action button(s) to show in header */
  actions?: React.ReactNode;
  /** Additional CSS classes for the container */
  className?: string;
  /** Header padding size */
  headerPadding?: 'sm' | 'md';
  /** Content padding size */
  contentPadding?: 'sm' | 'md' | 'lg';
  /** Show border around section */
  bordered?: boolean;
  /** Summary text shown inline when collapsed (e.g., "5 open · 3 done") */
  collapsedSummary?: string;
}

/**
 * Collapsible section component
 *
 * Features:
 * - Click header to expand/collapse
 * - Optional count badge
 * - Optional action buttons in header
 * - Controlled or uncontrolled mode
 * - Smooth transition (CSS-based)
 */
export function CollapsibleSection({
  title,
  count,
  defaultExpanded = false,
  expanded: controlledExpanded,
  onExpandedChange,
  children,
  actions,
  className = '',
  headerPadding = 'md',
  contentPadding = 'md',
  bordered = true,
  collapsedSummary,
}: CollapsibleSectionProps) {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const contentId = useId();

  // Use controlled state if provided, otherwise use internal state
  const isExpanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;

  const handleToggle = () => {
    const newExpanded = !isExpanded;
    if (controlledExpanded === undefined) {
      setInternalExpanded(newExpanded);
    }
    onExpandedChange?.(newExpanded);
  };

  const headerPaddingClasses = {
    sm: 'p-3',
    md: 'p-4',
  };

  // The header padding moved onto the toggle button (so its whole area still toggles); the actions keep the matching edge gap.
  const actionsPaddingClasses = {
    sm: 'pr-3',
    md: 'pr-4',
  };

  const contentPaddingClasses = {
    sm: 'px-3 pb-3',
    md: 'px-4 pb-4',
    lg: 'px-6 pb-6',
  };

  const body = (
    <>
      {/* Header - always visible. The toggle <button> and the actions are SIBLINGS: an interactive control inside a
          <button> is invalid HTML and unreliable for assistive tech. */}
      <div
        className={`w-full flex items-center justify-between ${isExpanded ? 'rounded-t-2xl' : 'rounded-2xl'} hover:bg-slate-50 transition-colors`}
      >
        <button
          type="button"
          onClick={handleToggle}
          aria-expanded={isExpanded}
          aria-controls={contentId}
          className={`flex-1 min-w-0 flex items-center gap-2 ${headerPaddingClasses[headerPadding]} ${isExpanded ? 'rounded-tl-2xl' : 'rounded-l-2xl'} text-left`}
        >
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-slate-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-slate-400" />
          )}
          <h3 className="font-medium text-slate-900">{title}</h3>
          {count !== undefined && (
            <span className="text-sm text-slate-500">({count})</span>
          )}
          {!isExpanded && collapsedSummary && (
            <span className="text-xs text-slate-400 ml-1">{collapsedSummary}</span>
          )}
        </button>

        {actions && <div className={actionsPaddingClasses[headerPadding]}>{actions}</div>}
      </div>

      {/* Content - shown when expanded */}
      {isExpanded && (
        <div id={contentId} className={contentPaddingClasses[contentPadding]}>
          {children}
        </div>
      )}
    </>
  );

  // The bordered surface IS a <Card> (rounded-2xl, border-slate-300, shadow-sm), so it cannot drift
  // from the one card chrome (#2869). padding="none": the header button and the content wrapper
  // carry their own padding. Unbordered keeps a bare white surface with the same radius.
  if (bordered) {
    return (
      <Card padding="none" className={className}>
        {body}
      </Card>
    );
  }
  return <div className={`bg-white rounded-2xl ${className}`.trim()}>{body}</div>;
}

export default CollapsibleSection;
